// @ts-check

import React, { useState } from "react";
import {
  Card,
  EmptySearchResult,
  IndexTable,
  InlineStack,
  Spinner,
  Text,
  useBreakpoints,
} from "@shopify/polaris";

import { UtmSetOrdersTableRow } from "./UtmSetOrdersTableRow";

import { useUtmSetContext } from "context/UtmSetContext";

import { ORDERS_PAGE_SIZE } from "utils/orders";

const TABLE_COLUMNS = [
  {
    label: "Order #",
    key: "order #",
  },
  {
    label: "Customer",
    key: "customer",
  },
  {
    label: "Date",
    key: "date",
  },
  {
    label: "Order Total",
    key: "total",
    numeric: true,
  },
];

export const UtmSetOrdersTable = () => {
  const {
    utmSetOrders,
    isFetchingUtmSetOrders,
    utmSetOrdersPage,
    setUtmSetOrdersPage,
    utmSetOrdersTotalCount,
  } = useUtmSetContext();

  const headings = TABLE_COLUMNS
    .map((column) => {
      return {
        id: column.label,
        alignment: column.numeric || column.value === "actions" ? "end" : "start",
        title: column.label,
      };
    });

  const emptyStateMarkup = (
    <EmptySearchResult
      title="No orders yet"
      description="You might not have received any orders for this UTM set. Try adjusting the date range in the top left."
      withIllustration
    />
  );

  const totalPages = ~~(utmSetOrdersTotalCount / ORDERS_PAGE_SIZE) + 1;

  const paginationLabel = (
    <InlineStack gap="300" align="center">
      <Text fontWeight="semibold" as="p">Page {utmSetOrdersPage} of {totalPages}</Text>
      <Text as="p">Showing {utmSetOrders.length} of {new Intl.NumberFormat("en-US").format(utmSetOrdersTotalCount)} orders</Text>
      {isFetchingUtmSetOrders && <Spinner size="small" />}
    </InlineStack>
  );

  return (
    <IndexTable
      condensed={useBreakpoints().smDown}
      resourceName={{ singular: "order", plural: "orders" }}
      itemCount={utmSetOrders.length}
      selectable={false}
      emptyState={emptyStateMarkup}
      headings={headings}
      lastColumnSticky
      pagination={{
        label: paginationLabel,
        hasPrevious: utmSetOrdersPage > 1,
        hasNext: ORDERS_PAGE_SIZE * utmSetOrdersPage < utmSetOrdersTotalCount,
        onNext: () => {
          setUtmSetOrdersPage(page => Math.min(totalPages, page + 1));
        },
        onPrevious: () => {
          setUtmSetOrdersPage(page => Math.max(0, page - 1));
        },
      }}
    >
      {utmSetOrders.map((row, index) => (
        <UtmSetOrdersTableRow
          key={index}
          index={index}
          data={row}
        />
      ))}
    </IndexTable>
  );
};
